import { SharedModals, TransactionsModals, TxResult } from "@/constants/enums";
import { useState } from "react";
import {
  AddTransactionModal,
  WithdrawalTransactionSummaryModal,
  TransactionResultModal
} from "./modals";
import { useCreateOffRamp } from "@/hooks/mutations";
import { useCurrentOrganization, useCurrentEntityDetail } from "@/hooks/queries";
import { LoadingModal } from "@/components/modals";
import { useTranslation } from "react-i18next";
import { FramerButton } from "@/components/motion";
import { OrganizationType } from "@/types/domain";
import { TokenOperationStatus, OffRampApprovalStatus } from "@/types/api/tokenOperation";

function Transaction({ disabled = false }: { disabled?: boolean }) {
  // States
  const [amount, setAmount] = useState<number>(0);
  const [transactionResultType, setTransactionResultType] = useState<TxResult>(TxResult.SUCCESS);
  const [showModal, setShowModal] = useState<TransactionsModals | SharedModals>(TransactionsModals.NoModal);

  // Query hooks
  const { data: organization } = useCurrentOrganization();
  const { data: entity } = useCurrentEntityDetail();

  // Mutation hooks
  const { mutateAsync: mutateOffRamp } = useCreateOffRamp();

  // Hooks
  const { t } = useTranslation(["components"]);

  // Off-ramp is submitted via API; result comes from the mutation settle.
  async function handleOffRampMutation() {
    if (!organization?.externalEntityId) return;
    if (
      organization.externalEntityType !== OrganizationType.Merchant
      && organization.externalEntityType !== OrganizationType.Financier
    ) {
      return;
    }
    if (amount === 0) return;

    setShowModal(TransactionsModals.Loading);

    try {
      const tokenOperation = await mutateOffRamp({
        externalEntityId: organization.externalEntityId,
        externalEntityType: organization.externalEntityType,
        amount,
        requestId: crypto.randomUUID(),
        publicKey: entity?.publicKey ?? undefined,
      });

      const approvalFailed =
        tokenOperation.approvalStatus === OffRampApprovalStatus.Failed ||
        !!tokenOperation.approvalError;

      if (tokenOperation.status === TokenOperationStatus.Failed || approvalFailed) {
        setTransactionResultType(TxResult.ERROR);
      } else if (tokenOperation.status === TokenOperationStatus.Confirmed) {
        setTransactionResultType(TxResult.SUCCESS);
      } else {
        setTransactionResultType(TxResult.PROCESSING);
      }
    } catch (err) {
      console.error("Off-ramp failed:", err);
      setTransactionResultType(TxResult.ERROR);
    } finally {
      setShowModal(TransactionsModals.TransactionResult);
    }
  }

  function handleTransactionOpen() {
    setShowModal(TransactionsModals.AddTransaction);
  }

  const sharedProps = {
    amount: amount,
    setAmount: setAmount,
    showModal: showModal,
    setShowModal: setShowModal,
  };

  return (
    <div>
      {/* // TODO: Fix button hover colour error */}
      <FramerButton
        whileHover={disabled ? undefined : { backgroundColor: "#EE5300", color: "#fff" }}
        onClick={() => handleTransactionOpen()}
        disabled={disabled}
        className="text-mforange dark:text-mforange border-mforange dark:border-mforange bg-transparent"
        variant="outline-primary"
      >
        <p className="lg:flex ml-1 text-base font-normal">
          {t("header.deposit.deposit")}<span className="hidden lg:inline">&nbsp;/&nbsp;{t("header.deposit.withdraw")}</span>
        </p>
      </FramerButton>

      {/* MODALS */}
      {showModal === TransactionsModals.AddTransaction &&
        <AddTransactionModal
          {...sharedProps}
        />
      }
      {showModal === TransactionsModals.WithdrawalSummary &&
        <WithdrawalTransactionSummaryModal
          amount={amount}
          showModal={showModal}
          setShowModal={setShowModal}
          handleTransactionMutation={handleOffRampMutation}
        />
      }
      {showModal === TransactionsModals.TransactionResult &&
        <TransactionResultModal
          {...sharedProps}
          transactionResultType={transactionResultType}
        />
      }
      {showModal === TransactionsModals.Loading &&
        <LoadingModal
          showModal={showModal}
          setShowModal={setShowModal}
          loadingText={t("toasts:sendingWithdrawal")}
          modalType={TransactionsModals.Loading}
        />
      }
    </div>
  );
}

export default Transaction;
